import { useState } from 'react'

import { useGameStore } from '../store/gameStore'

/**
 * The player's way into the stand when there is no operator in front of it.
 *
 * Deliberately the only thing on screen: the lobby behind a login form
 * would show games that cannot be opened, and a card that does nothing
 * when clicked reads as a broken build rather than as "sign in first".
 *
 * Accounts are created in the back office — there is no sign-up link here,
 * because there is no sign-up endpoint to link to.
 */
export function LoginScreen(): React.JSX.Element {
  const signIn = useGameStore((s) => s.signIn)

  const [login, setLogin] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await signIn(login, password)
    } catch {
      // One wording for a wrong password and an unknown login, matching
      // what the server answers: telling them apart would turn the form
      // into a list of which accounts exist.
      setError('Invalid login or password')
      setBusy(false)
    }
  }

  return (
    <div className="signin">
      <form className="signin-card" onSubmit={submit}>
        <h1 className="signin-title">Demo games</h1>
        <p className="signin-hint">Sign in to play. Accounts are issued by the operator.</p>

        {error && (
          <p className="signin-error" role="alert">
            {error}
          </p>
        )}

        <label className="signin-label" htmlFor="player-login">
          Login
        </label>
        <input
          id="player-login"
          className="signin-input"
          name="username"
          autoComplete="username"
          autoFocus
          value={login}
          onChange={(e) => setLogin(e.target.value)}
          required
        />

        <label className="signin-label" htmlFor="player-password">
          Password
        </label>
        <input
          id="player-password"
          className="signin-input"
          name="password"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />

        <button className="signin-button" type="submit" disabled={busy || !login || !password}>
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </div>
  )
}
